import { unitFor, useLocaleContext } from "../contexts/LocaleContext";

import { useEffect, useState } from "react";
import fetchChart from "../api/ng/chart";
import convert, { Units } from "../convert";
import { ISelectedSensor } from "../models/SelectedSensor";
import { SensorDisplayPrecip } from "../models/SensorDisplay";
import { isoToSeconds, toSeconds } from "../shared/duration";

export interface ChartApiResponseMeta {
    units: { [measure: string]: Units };
}
export interface ChartApiResponseData {
    ts: string;
    // would like value of measure index signature to be number, but:
    // https://www.typescriptlang.org/docs/handbook/2/objects.html#index-signatures
    [measure: string]: any;
}

export interface ChartApiResponse {
    meta: ChartApiResponseMeta;
    data: ChartApiResponseData[];
}

export type Datum = {
    ts: Date;
    value: number;
}

export interface ChartData {
    data: Datum[];
    unit: Units;
}

const useChartData = (selected: ISelectedSensor) => {
    const { units } = useLocaleContext();
    const { display } = selected;
    const toUnit = unitFor(units, display.measure);

    const [response, setResponse] = useState<ChartApiResponse>()

    useEffect(() => {
        const fetchData = async () => {
            const response = await fetchChart(selected);
            setResponse(response);
        }
        fetchData();
    }, [selected]);

    if (response) {
        const displayMeasure = SensorDisplayPrecip.is(display) ? "precip" : display.measure;
        const { meta, data } = response;

        const metaMeasure = Object.keys(meta.units).find(k => k.startsWith(displayMeasure));
        if (metaMeasure === undefined) {
            setResponse(undefined);
            return;
        }

        const multiplier = display.measure === "intensity"
            ? toSeconds({ hours: 1 }) / isoToSeconds(display.interval)
            : 1;

        const fromUnit = meta.units[metaMeasure];

        const measureData: Datum[] = data.map((d) => ({
            ts: new Date(d.ts),
            value: convert(d[metaMeasure] * multiplier).from(fromUnit).to(toUnit)
        }));

        const chartData: ChartData = {
            data: measureData,
            unit: toUnit
        };
        return chartData;
    }
};

export default useChartData;
